import React, { useEffect, useRef, useState } from "react";

import ipaddr from "ipaddr.js";

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  TextInput,
  Tooltip,
} from "@narmi/design_system";

import { useLocalization } from "@fluent/react";

import { destroyBankingToken, getClientIp, getScopeGroups } from "./api";

import type { ScopeGroup } from "./api";
import {
  DeserializedBankingToken,
  DialogProps,
  TokenSubmitData,
} from "./types";
import { getAlertError, getApiErrorMessages } from "./modals";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";

import styles from "./tokenForm.module.scss";

/**
 * Derives which scope columns are assignable from the institution's
 * `business_banking_api_allowed_scopes` config. The list may hold coarse action
 * keywords (`"read"`/`"write"`) or full scope strings (`"banking:accounts:read"`);
 * an action is allowed when it appears in either form. An unset/empty list places
 * no restriction, so both columns are allowed. A disallowed column keeps its
 * header (so the Read/Write grid layout is preserved) but omits the per-row
 * checkboxes, and its select-all header checkbox is disabled.
 */
const getAllowedScopeColumns = (allowedScopes?: string[]) => {
  if (!allowedScopes?.length) {
    return { readAllowed: true, writeAllowed: true };
  }
  const allows = (action: "read" | "write") =>
    allowedScopes.some(
      (scope) => scope === action || scope.endsWith(`:${action}`),
    );
  return { readAllowed: allows("read"), writeAllowed: allows("write") };
};

const isValidIP = (input: string) => {
  try {
    const [ip, mask] = input.split("/");
    const parsedIP = ipaddr.parse(ip);

    // If there's no CIDR mask, it's a valid standalone IP
    if (mask === undefined) return true;

    // Validate CIDR mask
    const maskNum = Number(mask);
    return parsedIP.kind() === "ipv4"
      ? maskNum >= 0 && maskNum <= 32
      : maskNum >= 0 && maskNum <= 128;
  } catch {
    return false;
  }
};

export const TokenDialogSubmitFooter = ({
  buttonText,
  onCancel,
  onConfirm,
}: {
  buttonText: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { l10n } = useLocalization();
  return (
    <div className={styles.footer}>
      <Button kind="plain" onClick={onCancel}>
        {l10n.getString("button-cancel")}
      </Button>
      <Button onClick={onConfirm}>{buttonText}</Button>
    </div>
  );
};

const TokenWriteForm = ({
  nickname,
  onChangeNickname,
  nicknameError,
  ipAllowList,
  onChangeIpAllowList,
  ipAllowListError,
  scopes,
  onChangeScopes,
  scopeGroups,
  alertEl,
  readOnly = false,
}: {
  nickname: string;
  onChangeNickname: (val: string) => void;
  nicknameError?: string | null;
  ipAllowList: string;
  onChangeIpAllowList: (val: string) => void;
  ipAllowListError?: string | null;
  scopes: Set<string>;
  onChangeScopes: (val: Set<string>) => void;
  scopeGroups: ScopeGroup[];
  alertEl: React.ReactNode;
  readOnly?: boolean;
}) => {
  const { l10n } = useLocalization();
  const { features } = useInstitutionSettings();
  const { readAllowed, writeAllowed } = getAllowedScopeColumns(
    features?.business_banking_api_allowed_scopes,
  );
  const [scopeSearch, setScopeSearch] = useState("");
  const alertRef = useRef<HTMLDivElement>(null);
  // Bring the error banner into view when it appears, since submit can be
  // triggered while scrolled down near the scopes list / submit button.
  useEffect(() => {
    if (alertEl) {
      alertRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [alertEl]);
  const handleScopeChange = (scope: string) => {
    scopes.has(scope) ? scopes.delete(scope) : scopes.add(scope);
    onChangeScopes(new Set(scopes));
  };

  // Filter the rendered scope rows by resource name. Deriving the select-all
  // state and column scope lists from the filtered set keeps the header
  // checkboxes consistent with what's actually visible.
  const normalizedSearch = scopeSearch.trim().toLowerCase();
  const visibleScopeGroups = normalizedSearch
    ? scopeGroups.filter(
        (group) =>
          group.label.toLowerCase().includes(normalizedSearch) ||
          group.resource.toLowerCase().includes(normalizedSearch),
      )
    : scopeGroups;

  // Left-aligned Read/Write columns each get a "select all" header checkbox that
  // toggles every scope in that column. Scope groups come from the permission-
  // filtered schema, so no scope values are hardcoded here.
  const readScopes = visibleScopeGroups
    .map((group) => group.read)
    .filter(Boolean) as string[];
  const writeScopes = visibleScopeGroups
    .map((group) => group.write)
    .filter(Boolean) as string[];
  const allReadSelected =
    readScopes.length > 0 && readScopes.every((scope) => scopes.has(scope));
  const someReadSelected = readScopes.some((scope) => scopes.has(scope));
  const allWriteSelected =
    writeScopes.length > 0 && writeScopes.every((scope) => scopes.has(scope));
  const someWriteSelected = writeScopes.some((scope) => scopes.has(scope));

  const toggleAll = (columnScopes: string[], allSelected: boolean) => {
    const next = new Set(scopes);
    columnScopes.forEach((scope) =>
      allSelected ? next.delete(scope) : next.add(scope),
    );
    onChangeScopes(next);
  };

  return (
    <div className={styles.form}>
      <div className={styles.fieldColumn}>
        <TextInput
          label={l10n.getString("textinput-label-nickname")}
          value={nickname}
          disabled={readOnly}
          error={nicknameError}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChangeNickname(e.target.value)
          }
        />
      </div>
      <div className={styles.ipRow}>
        <div className={styles.ipField}>
          <TextInput
            label={l10n.getString("textinput-label-ip-allowlist")}
            value={ipAllowList}
            disabled={readOnly}
            error={ipAllowListError}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeIpAllowList(e.target.value)
            }
            endContent={
              <Tooltip text={l10n.getString("tooltip-ip-allowlist-format")}>
                <span
                  className="narmi-icon-info"
                  role="img"
                  aria-label={l10n.getString("tooltip-ip-allowlist-format")}
                  tabIndex={0}
                ></span>
              </Tooltip>
            }
          />
        </div>
        {!readOnly && (
          <Button
            kind="plain"
            onClick={() => {
              getClientIp().then((ipResponse) =>
                onChangeIpAllowList(ipResponse.ip),
              );
            }}
            label={l10n.getString("button-use-my-ip")}
          />
        )}
      </div>
      <div className={styles.divider} />
      <h3 className={`fontSize--heading3 fontWeight--bold ${styles.scopesTitle}`}>
        {l10n.getString("form-section-scopes")}
      </h3>
      <div className={styles.scopesAlert}>
        <Alert isActive kind="info" isDismissable={false}>
          {l10n.getString("alert-scopes-permission-info")}
        </Alert>
      </div>
      {alertEl && <div ref={alertRef}>{alertEl}</div>}
      {scopeGroups.length > 0 && (
        <>
          <div className={styles.scopeHeaderRow}>
            <div className={`${styles.grow} ${styles.scopesSearch}`}>
              <TextInput
                label={l10n.getString("search-placeholder-scopes")}
                value={scopeSearch}
                startIcon="search"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setScopeSearch(e.target.value)
                }
              />
            </div>
            <div className={styles.scopeColumn}>
              <Checkbox
                label={l10n.getString("checkbox-label-read")}
                aria-label={l10n.getString("aria-label-select-all-read")}
                checked={allReadSelected}
                indeterminate={someReadSelected && !allReadSelected}
                onChange={() => toggleAll(readScopes, allReadSelected)}
                // Disabled when the action is disallowed: the column header stays
                // for layout, but its (hidden) per-row scopes can't be bulk-toggled.
                isDisabled={readOnly || !readAllowed}
                testId="scope-select-all-read"
              />
            </div>
            <div className={styles.scopeColumn}>
              <Checkbox
                label={l10n.getString("checkbox-label-write")}
                aria-label={l10n.getString("aria-label-select-all-write")}
                checked={allWriteSelected}
                indeterminate={someWriteSelected && !allWriteSelected}
                onChange={() => toggleAll(writeScopes, allWriteSelected)}
                isDisabled={readOnly || !writeAllowed}
                testId="scope-select-all-write"
              />
            </div>
          </div>
          <div
            role="group"
            aria-label={l10n.getString("form-section-scopes")}
            className={styles.scopeCard}
          >
            {visibleScopeGroups.length === 0 && (
              <div className={styles.scopeEmptyState}>
                {l10n.getString("text-no-scopes-found")}
              </div>
            )}
            {visibleScopeGroups.map((group, idx) => (
              <div
                key={group.resource}
                className={
                  idx === 0
                    ? styles.scopeRow
                    : `${styles.scopeRow} ${styles.scopeRowDivided}`
                }
              >
                <div className={styles.grow}>
                  <div>{group.label}</div>
                  <div className={styles.scopeSubtext}>
                    {`banking:${group.resource}`}
                  </div>
                </div>
                <div className={styles.scopeColumn}>
                  {readAllowed && group.read && (
                    <Checkbox
                      label=""
                      aria-label={l10n.getString("aria-label-scope-read", {
                        resource: group.label,
                      })}
                      checked={scopes.has(group.read)}
                      onChange={() => handleScopeChange(group.read as string)}
                      isDisabled={readOnly}
                      testId={`scope-${group.resource}-read`}
                    />
                  )}
                </div>
                <div className={styles.scopeColumn}>
                  {writeAllowed && group.write && (
                    <Checkbox
                      label=""
                      aria-label={l10n.getString("aria-label-scope-write", {
                        resource: group.label,
                      })}
                      checked={scopes.has(group.write)}
                      onChange={() => handleScopeChange(group.write as string)}
                      isDisabled={readOnly}
                      testId={`scope-${group.resource}-write`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const useTokenForm = ({
  onSubmit,
  updateToken,
  readOnly = false,
}: {
  onSubmit: (data: TokenSubmitData) => Promise<void> | void;
  updateToken?: DeserializedBankingToken;
  readOnly?: boolean;
}) => {
  const { l10n } = useLocalization();
  const initialNickName = updateToken?.nickname || "";
  const initialScopes: Set<string> = updateToken?.scopes || new Set();
  const initialIpAllowlist = updateToken?.ipAllowlist.join(", ") || "";

  const [nickname, setNickName] = useState(initialNickName);
  const [ipAllowList, setIpAllowList] = useState(initialIpAllowlist);
  const [scopes, setScopes] = useState(initialScopes);
  const [alertEl, setAlertEl] = useState<React.ReactNode>(null);
  const [scopeGroups, setScopeGroups] = useState<ScopeGroup[]>([]);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [ipAllowListError, setIpAllowListError] = useState<string | null>(null);

  useEffect(() => {
    getScopeGroups().then((groups) => {
      if (Array.isArray(groups)) setScopeGroups(groups);
    });
  }, []);

  const handleChangeNickname = (val: string) => {
    setNickName(val);
    if (nicknameError) setNicknameError(null);
  };

  const handleChangeIpAllowList = (val: string) => {
    setIpAllowList(val);
    if (ipAllowListError) setIpAllowListError(null);
  };

  const clearForm = () => {
    setNickName("");
    setIpAllowList("");
    setScopes(new Set());
    setAlertEl(null);
    setNicknameError(null);
    setIpAllowListError(null);
  };

  const validate = () => {
    // Surface required fields inline so the user sees which input needs input,
    // alongside the general banner.
    const requiredMessage = l10n.getString("error-field-required");
    const missingNickname = !nickname;
    const missingIp = !ipAllowList;
    setNicknameError(missingNickname ? requiredMessage : null);
    setIpAllowListError(missingIp ? requiredMessage : null);
    if (missingNickname || missingIp) {
      setAlertEl(
        getAlertError(l10n.getString("alert-all-fields-required"), () =>
          setAlertEl(null),
        ),
      );
      return false;
    }
    if (scopes.size === 0) {
      setAlertEl(
        getAlertError(l10n.getString("alert-scopes-required"), () =>
          setAlertEl(null),
        ),
      );
      return false;
    }
    ipAllowList.split(",").forEach((ip) => {
      if (!isValidIP(ip.trim())) {
        setIpAllowListError(l10n.getString("alert-invalid-ip-address"));
        setAlertEl(
          getAlertError(l10n.getString("alert-invalid-ip-address"), () =>
            setAlertEl(null),
          ),
        );
      }
    });
    return true;
  };

  const handleConfirm = async () => {
    setAlertEl(null);
    if (!validate()) return;
    const data = {
      nickname,
      scope: Array.from(scopes),
      ip_allowlist: ipAllowList.split(",").map((ip) => ip.trim()),
    };
    try {
      await onSubmit(data);
    } catch (error) {
      const messages = getApiErrorMessages(error);
      setAlertEl(
        getAlertError(
          messages.length
            ? messages.join(" ")
            : l10n.getString("alert-token-save-failed"),
          () => setAlertEl(null),
        ),
      );
    }
  };

  const tokenForm = (
    <TokenWriteForm
      nickname={nickname}
      onChangeNickname={handleChangeNickname}
      nicknameError={nicknameError}
      ipAllowList={ipAllowList}
      onChangeIpAllowList={handleChangeIpAllowList}
      ipAllowListError={ipAllowListError}
      scopes={scopes}
      onChangeScopes={setScopes}
      scopeGroups={scopeGroups}
      alertEl={alertEl}
      readOnly={readOnly}
    />
  );

  return {
    tokenForm,
    alertEl,
    clearForm,
    handleConfirm,
  };
};

export const TokenRevokeDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
  tokenForRevoke,
  runWrite,
}: DialogProps & {
  tokenForRevoke: DeserializedBankingToken | null;
  runWrite: <T>(write: () => Promise<T>) => Promise<T>;
}) => {
  const { l10n } = useLocalization();
  if (!tokenForRevoke) {
    return null;
  }

  return (
    <Dialog
      title={l10n.getString("dialog-revoke-token-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      footer={
        <TokenDialogSubmitFooter
          buttonText={l10n.getString("button-revoke-token")}
          onCancel={onUserDismiss}
          onConfirm={() =>
            runWrite(() => destroyBankingToken(tokenForRevoke.uuid))
              .then(onConfirm)
              .catch(() => {})
          }
        />
      }
    >
      <p>{l10n.getString("dialog-revoke-token-message")}</p>
    </Dialog>
  );
};
